import { FC } from "react";
import { useContactLensesStore, useStepStore } from "@/store";
import { useTranslation } from "@/i18n";
import { Price } from "@/components/UI/Price";
import styles from "./PricesRecap.module.css";

/**
 * Parses a price string like "$126.99" to a number
 */
const parsePrice = (price: string): number => {
  return parseFloat(price.replace(/[$,]/g, ""));
};

/**
 * Formats a number to a price string like "$126.99"
 */
const formatPrice = (price: number): string => {
  return `$${price.toFixed(2)}`;
};

export const PricesRecap: FC = () => {
  const { contactLensesData, insDiscount } = useContactLensesStore();
  const { isInsuranceLoading } = useStepStore();
  const { t } = useTranslation();

  if (!contactLensesData) {
    return null;
  }

  const { OD, OS } = contactLensesData;

  // Get prices from first available product (they should be the same)
  const odProduct = OD?.product;
  const osProduct = OS?.product;
  let currentProduct = null;

  // We have 2 products
  if (odProduct && osProduct) {
    // i have both product IDs so it means we are editing from cart and we default to the OD product
    if (odProduct?.orderItemId && osProduct?.orderItemId) {
      currentProduct = odProduct;
      // if we have only one product with an id we set it means we entered the configuratore with the product without the id
    } else if (odProduct?.orderItemId && !osProduct?.orderItemId) {
      currentProduct = osProduct;
    } else if (!odProduct?.orderItemId && osProduct?.orderItemId) {
      currentProduct = odProduct;
    } else {
      // if there is no orderItemId we set the first product since it means we entered the configurator with identical products
      currentProduct = odProduct;
    }
  } else {
    // we set the product to the one we have
    currentProduct = odProduct || osProduct;
  }

  if (!currentProduct) {
    return null;
  }

  const boxListPrice = parsePrice(currentProduct.boxListPrice);
  const boxOfferPrice = currentProduct.boxOfferPrice
    ? parsePrice(currentProduct.boxOfferPrice)
    : undefined;

  // Calculate totals
  let odTotalListPrice, osTotalListPrice;
  let odBoxOfferPrice, osBoxOfferPrice;
  if (odProduct) {
    odTotalListPrice =
      parsePrice(odProduct?.boxListPrice) * odProduct.boxQuantity;
    odBoxOfferPrice = odProduct.boxOfferPrice
      ? parsePrice(odProduct.boxOfferPrice) * odProduct.boxQuantity
      : undefined;
  }
  if (osProduct) {
    osTotalListPrice =
      parsePrice(osProduct?.boxListPrice) * osProduct.boxQuantity;
    osBoxOfferPrice = osProduct.boxOfferPrice
      ? parsePrice(osProduct.boxOfferPrice) * osProduct.boxQuantity
      : undefined;
  }

  const totalListPrice = (odTotalListPrice || 0) + (osTotalListPrice || 0);
  let totalOfferPrice: number | undefined =
    (odBoxOfferPrice || 0) + (osBoxOfferPrice || 0);
  const totalInsPrice = insDiscount
    ? (totalOfferPrice !== 0 ? totalOfferPrice : totalListPrice) - insDiscount
    : undefined;

  const boxInsPriceNum = insDiscount
    ? boxOfferPrice !== undefined
      ? boxOfferPrice
      : boxListPrice
    : undefined;

  if (odBoxOfferPrice === undefined && osBoxOfferPrice === undefined) {
    totalOfferPrice = undefined;
  }

  const isBoxIncluded =
    (boxInsPriceNum !== undefined && boxInsPriceNum <= 0) ||
    (boxOfferPrice !== undefined && boxOfferPrice <= 0);
  const isTotalIncluded =
    (totalInsPrice !== undefined && totalInsPrice <= 0) ||
    (totalOfferPrice !== undefined && totalOfferPrice <= 0);

  return (
    <div className={styles.pricesRecap}>
      <div className={styles.priceRow}>
        <span className={styles.label}>{t("pricesRecap.pricePerBox")}</span>
        <Price
          loading={isInsuranceLoading}
          listPrice={formatPrice(boxListPrice)}
          offerPrice={
            boxOfferPrice !== undefined ? formatPrice(boxOfferPrice) : undefined
          }
          insPrice={
            insDiscount
              ? boxOfferPrice !== undefined
                ? formatPrice(boxOfferPrice)
                : undefined
              : undefined
          }
          included={isBoxIncluded}
          includedLabel={t("pricesRecap.included")}
          listPriceClassName={styles.boxListPrice}
        />
      </div>
      <div className={styles.totalRow}>
        <div className={styles.totalLabel}>
          <span className={styles.totalText}>{t("pricesRecap.total")}</span>
          <span className={styles.boxesCount}>
            ({(osProduct?.boxQuantity || 0) + (odProduct?.boxQuantity || 0)}{" "}
            {t("pricesRecap.boxes")})
          </span>
        </div>
        <Price
          loading={isInsuranceLoading}
          listPrice={formatPrice(totalListPrice)}
          offerPrice={
            totalOfferPrice !== undefined
              ? formatPrice(totalOfferPrice)
              : undefined
          }
          insPrice={totalInsPrice ? formatPrice(totalInsPrice) : undefined}
          included={isTotalIncluded}
          includedLabel={t("pricesRecap.included")}
          listPriceClassName={styles.totalListPrice}
          displayPriceClassName={styles.totalPrice}
        />
      </div>
    </div>
  );
};
